import React, { useState } from 'react';
import { Modal, Checkbox } from 'antd';
import { Button } from '@/components/Button';
import styles from './HtsQuestionnaire.module.scss';

interface HtsWaiverModalProps {
  // Server-decided (see the questions route), not useIsGuest.
  isGuest: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

// If either notice below ever changes, bump that variant's HTS_WAIVER_VERSION
// (@evaltick/shared) in the same change — both for the shared checkbox label.
// That's the version each submission records as its consent record.

export const HtsWaiverModal: React.FC<HtsWaiverModalProps> = ({ isGuest, onAccept, onDecline }) => {
  const [consented, setConsented] = useState(false);

  return (
    <Modal
      open
      closable={false}
      maskClosable={false}
      footer={null}
      width={600}
      centered
    >
      <div className={styles.waiverHead}>
        <h2 className={styles.waiverTitle}>Disclosure &amp; Consent</h2>
      </div>

      <div className={styles.waiverBody}>
        {isGuest ? <GuestDisclosure /> : <CandidateDisclosure />}
      </div>

      <label className={styles.consentRow}>
        <Checkbox checked={consented} onChange={(e) => setConsented(e.target.checked)} />
        <span className={styles.consentText}>I agree</span>
      </label>

      <div className={styles.waiverActions}>
        <Button appearance="outline" onClick={onDecline}>Decline &amp; go back</Button>
        <Button appearance="solid" onClick={onAccept} disabled={!consented}>
          Continue
        </Button>
      </div>
    </Modal>
  );
};

// Access-code (agency) candidates: de-identified, report goes to the agency.
const GuestDisclosure: React.FC = () => (
  <>
    <p>
      <strong>High-Trust Suitability (HTS) Evaluation – Agency Candidates</strong>
    </p>
    <p>
      This notice is from Evaltick Technologies Inc. (“Evaltick”, “we”, “us”). If a hiring agency
      asked you to complete this evaluation and gave you an access code, please read this notice
      before you start.
    </p>

    <h4>Your identity is protected</h4>
    <ul>
      <li>
        <strong>Evaltick never knows who you are.</strong> We do not collect your name or contact
        information.
      </li>
      <li>
        <strong>Your code is your only link.</strong> Your responses and your report are linked only
        to the access code the agency gave you.
      </li>
      <li>
        <strong>Only the agency can connect the code to you.</strong> The agency keeps the record
        that connects your code to your name. Evaltick does not have this record and cannot link
        your code to you.
      </li>
    </ul>

    <h4>Purpose</h4>
    <p>
      This evaluation collects background and lifestyle information that hiring agencies commonly
      assess for high-trust roles. The questions cover personal history, associations, character,
      driving record, substance use, and general health.
    </p>
    <p>
      We use your responses to produce an evaluation report for the agency that issued your access
      code. A trained Evaltick Assessor reviews your results and may clarify some answers with you
      via the system before we send the report.
    </p>
    <p>
      Some questions ask for sensitive information, such as health and substance use. We collect
      this information only with your express consent.
    </p>

    <h4>Keep your answers de-identified</h4>
    <p>
      Do not write your name, address, phone number, email, or other contact details in any answer.
      If you name other people, use their role only (for example, “my former supervisor”). This
      keeps your responses linked to your code and not to your identity.
    </p>

    <h4>Voluntary participation</h4>
    <p>
      Answering is voluntary. You may stop at any time. If you do not complete the evaluation, we
      cannot produce a complete report. The agency decides how this affects your application.
    </p>

    <h4>Who sees your responses</h4>
    <ul>
      <li>
        <strong>The agency:</strong> The agency that issued your access code receives your
        evaluation report, linked to your code. We do not send it to any other agency or
        organization.
      </li>
      <li>
        <strong>Evaltick:</strong> Authorized Evaltick staff and Assessors can access your coded
        responses to produce your report. They do not know who you are.
      </li>
      <li>
        <strong>Service providers:</strong> Our service providers must keep the de-identified
        information confidential. All data is stored in Canada.
      </li>
    </ul>
    <p>
      We apply industry-standard encryption at rest and in transit. We never sell your responses. We
      do not disclose them to anyone else, except as this notice describes or where the law
      requires us to do so.
    </p>
    <p>
      After the agency receives your report, the agency’s own privacy rules apply to its copy. The
      agency can connect the report to your name.
    </p>

    <h4>Accuracy &amp; honesty</h4>
    <p>
      Please answer truthfully, to the best of your knowledge. The agency may compare your answers
      with the results of its own background investigation. If the agency finds a
      misrepresentation, it can disqualify you immediately. This can happen even if the matter
      itself was manageable.
    </p>

    <h4>Data retention &amp; deletion</h4>
    <p>We keep your coded responses for two years. After this period, we delete them.</p>
    <p>
      You may ask us to delete your responses at any time. Contact the agency and ask them to
      contact us on your behalf. If you contact us directly you compromise the integrity of the
      deidentified agency code.
    </p>
    <p>
      Deletion does not remove a report that the agency already received. To ask about that copy,
      contact the agency.
    </p>

    <h4>Withdrawing consent</h4>
    <p>
      You may withdraw your consent at any time. To do this, ask the agency to contact Evaltick and
      give your access code. If you withdraw before we send the report, we do not send it.
    </p>

    <h4>Questions and complaints</h4>
    <p>
      Our Privacy Officer is responsible for how we handle your information. Contact:
      hello@evaltick.com
    </p>

    <h4>Not a background check</h4>
    <p>
      This evaluation is not a formal background check. The results of the agency’s formal
      background investigation may be different from the results of this evaluation.
    </p>

    <h4>Your consent</h4>
    <p>
      By selecting “I agree”, you confirm that you read this notice. You give your express consent
      to:
    </p>
    <ul>
      <li>the collection and use of your coded responses as this notice describes, and</li>
      <li>the disclosure of your evaluation report to the agency that issued your access code.</li>
    </ul>
  </>
);

// Signed-in (coaching) candidates: results stay with the candidate.
const CandidateDisclosure: React.FC = () => (
  <>
    <p>
      <strong>Readiness Assessment – Coaching Candidates</strong>
    </p>
    <p>
      This notice is from Evaltick Technologies Inc. (“Evaltick”, “we”, “us”). Please read it
      before you start your readiness assessment. It applies to all candidates who prepare for
      high-trust roles.
    </p>

    <h4>How this assessment works for you</h4>
    <ul>
      <li>
        <strong>You are preparing on your own.</strong> You complete the assessment on your own to
        prepare for a high-trust role.
      </li>
      <li>
        <strong>You get feedback.</strong> We use your responses to show your strengths and the
        areas you need to work on.
      </li>
      <li>
        <strong>Your results stay with you.</strong> We do not share your responses or results with
        any hiring agency without your express consent.
      </li>
    </ul>

    <h4>Purpose</h4>
    <p>
      This assessment helps you find out how ready you are to apply for a high-trust role. It can
      include:
    </p>
    <ul>
      <li>questions to confirm that you meet the basic requirements for the role,</li>
      <li>behavioural and situational interview questions, and</li>
      <li>feedback on each competency, scored against structured rubrics.</li>
    </ul>
    <p>
      Your results show which competencies are a strength, which are developing, and which need
      focus. You can use this feedback to build your preparation plan.
    </p>
    <p>
      Some packages may also include High-Trust Suitability (HTS) questions about background and
      lifestyle. These questions can ask for sensitive information, such as health and substance
      use. We collect this information only with your express consent.
    </p>

    <h4>Voluntary participation</h4>
    <p>
      Answering is voluntary. You may stop at any time. If you skip questions or stop, our feedback
      may be less specific. This does not stop you from using the platform.
    </p>

    <h4>Who sees your responses</h4>
    <p>
      Only authorized Evaltick staff, your coach, and our service providers can access your
      responses. We do not share your responses or results with any hiring agency without your
      express consent.
    </p>
    <p>
      Our service providers must keep your information confidential. All data is stored in Canada.
      We apply industry-standard encryption at rest and in transit.
    </p>
    <p>
      We never sell your responses. We do not disclose them to anyone else, except as this notice
      describes or where the law requires us to do so.
    </p>

    <h4>Accuracy &amp; honesty</h4>
    <p>
      Please answer as you would in a real interview. Honest answers give you accurate feedback. If
      you overstate your experience here, your results will not show where you need to improve.
    </p>
    <p>
      This is important because in the real interview, hiring agencies check your history. If a
      formal background investigation finds a misrepresentation, the agency can disqualify you
      immediately. So practicing with authentic answers will give you the most benefit.
    </p>

    <h4>Data retention &amp; deletion</h4>
    <p>
      We keep your responses for a minimum of one year. You may ask us to delete your data at any
      time. Contact hello@evaltick.com.
    </p>

    <h4>Withdrawing consent</h4>
    <p>
      You may withdraw your consent at any time. Contact hello@evaltick.com. If you withdraw
      consent, we stop using your responses. Our feedback may be less specific.
    </p>

    <h4>Questions and complaints</h4>
    <p>
      Our Privacy Officer is responsible for how we handle your information. Contact:
      hello@evaltick.com
    </p>

    <h4>Not a hiring decision</h4>
    <p>
      This assessment is a preparation tool only. It is not a hiring decision, a formal test, or a
      background check. Your results in a hiring agency’s formal process may be different from your
      results here.
    </p>

    <h4>Your consent</h4>
    <p>
      By selecting “I agree”, you confirm that you read this notice. You give your express consent
      to the collection and use of your information as this notice describes.
    </p>
  </>
);
